// =============================================================================
// PeriodSelector — selector de rango temporal para el dashboard
// Presets: Hoy / Esta semana / Este mes / Últimos 30 días / Personalizado
// Default: Esta semana
//
// v1.2 — Fix del bug "Parametros from y to obligatorios cuando preset=custom":
// al pulsar "Personalizado" YA NO se dispara onChange. Se muestran los dos
// inputs de fecha y solo se aplica el cambio cuando el usuario pulsa "Aplicar"
// con ambas fechas rellenas. Durante ese intermedio, el resto del dashboard
// sigue mostrando los datos del preset anterior, sin errores.
// =============================================================================

import { useState, useEffect } from 'react';
import type { DashboardPreset } from '../../types/dashboard';

interface PeriodSelectorProps {
  preset: DashboardPreset;
  from: string | null;
  to: string | null;
  onChange: (p: { preset: DashboardPreset; from: string | null; to: string | null }) => void;
  disabled?: boolean;
}

const PRESETS: { value: DashboardPreset; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'week',  label: 'Esta semana' },
  { value: 'month', label: 'Este mes' },
  { value: '30d',   label: 'Últimos 30 días' },
  { value: 'custom', label: 'Personalizado' },
];

export function PeriodSelector({ preset, from, to, onChange, disabled }: PeriodSelectorProps) {
  // visualPreset: qué botón se muestra como activo visualmente.
  // Puede ir por delante del preset aplicado cuando el usuario pulsa
  // "Personalizado" pero aún no ha aplicado un rango.
  const [visualPreset, setVisualPreset] = useState<DashboardPreset>(preset);
  const [localFrom, setLocalFrom] = useState(from || '');
  const [localTo, setLocalTo] = useState(to || '');

  // Sincronizar cuando el preset externo cambia (por cambios de otras fuentes)
  useEffect(() => {
    setVisualPreset(preset);
  }, [preset]);

  const handlePresetClick = (p: DashboardPreset) => {
    if (disabled) return;
    setVisualPreset(p);
    if (p === 'custom') {
      // Solo cambio visual. No disparamos onChange: esperamos "Aplicar" con fechas.
      return;
    }
    onChange({ preset: p, from: null, to: null });
  };

  const applyCustom = () => {
    if (!localFrom || !localTo || disabled) return;
    onChange({ preset: 'custom', from: localFrom, to: localTo });
  };

  const canApply = Boolean(localFrom && localTo) && !disabled;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map((p) => {
        const isActive = visualPreset === p.value;
        return (
          <button
            key={p.value}
            type="button"
            disabled={disabled}
            onClick={() => handlePresetClick(p.value)}
            className={`
              px-3 py-1.5 rounded-xl text-sm font-medium
              transition-all duration-200 border
              ${isActive
                ? 'bg-primary text-white border-primary shadow-primary'
                : 'bg-white text-slate-600 border-slate-200 hover:border-primary/30 hover:text-slate-900'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {p.label}
          </button>
        );
      })}

      {visualPreset === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 sm:ml-2">
          <input
            type="date"
            value={localFrom}
            onChange={(e) => setLocalFrom(e.target.value)}
            max={localTo || undefined}
            className="px-3 py-1.5 rounded-xl text-sm border border-slate-200 text-slate-700 focus:outline-none focus:border-primary"
            disabled={disabled}
          />
          <span className="text-slate-400 text-sm">→</span>
          <input
            type="date"
            value={localTo}
            onChange={(e) => setLocalTo(e.target.value)}
            min={localFrom || undefined}
            className="px-3 py-1.5 rounded-xl text-sm border border-slate-200 text-slate-700 focus:outline-none focus:border-primary"
            disabled={disabled}
          />
          <button
            type="button"
            onClick={applyCustom}
            disabled={!canApply}
            className="px-3 py-1.5 rounded-xl text-sm font-medium bg-primary text-white shadow-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Aplicar
          </button>
        </div>
      )}
    </div>
  );
}
